import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking and performance monitoring.
 *
 * Required environment variables:
 * - VITE_SENTRY_DSN: Sentry project DSN
 * - VITE_APP_VERSION: Application version for release tracking
 * - VITE_ENVIRONMENT: Environment name (production, staging, development)
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize in production or if DSN is explicitly provided
  if (!dsn) {
    if (import.meta.env.PROD) {
      console.warn('Sentry DSN not configured. Error tracking disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_ENVIRONMENT || 'production',
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay for error context (masks all text by default for privacy)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Trace propagation targets for distributed tracing
    tracePropagationTargets: [
      ...(import.meta.env.DEV ? ['localhost'] : []),
      /^https:\/\/api\./,
    ],

    // Performance monitoring sample rate (10% of transactions)
    tracesSampleRate: 0.1,

    // Session replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Filter and sanitize data before sending
    beforeSend(event) {
      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      // Remove sensitive query parameters
      if (event.request?.query_string && typeof event.request.query_string === 'string') {
        const sensitiveParams = ['token', 'password', 'secret', 'key'];
        sensitiveParams.forEach((param) => {
          const queryString = event.request?.query_string;
          if (typeof queryString === 'string' && queryString.includes(param)) {
            event.request!.query_string = queryString.replace(
              new RegExp(`${param}=[^&]*`, 'g'),
              `${param}=[REDACTED]`
            );
          }
        });
      }

      return event;
    },

    // Filter breadcrumbs to reduce noise
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console.log breadcrumbs in production
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      return breadcrumb;
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.teletrader.com/',
      // Facebook borance
      'fb_xd_fragment',
      // Chrome extensions
      /^chrome-extension:\/\//,
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Ignore ResizeObserver errors (common, non-critical)
      'ResizeObserver loop',
    ],
  });
}

/**
 * Set user context for error tracking.
 * Call this after user login to associate errors with users.
 */
export function setSentryUser(user: { id: string; email?: string; name?: string } | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add a breadcrumb for tracking user actions.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  });
}

/**
 * Capture an exception with optional context.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): string {
  return Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message for non-error events that should be tracked.
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): string {
  return Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

// Re-export Sentry for advanced usage
export { Sentry };
